/**
 * Présence en ligne dans la messagerie : règles d'affichage pures, sans réseau ni état.
 * Le serveur dit si une personne est en ligne (dernier signal de moins de 75 secondes) et quand
 * elle a été vue ; l'écran le formule.
 */
export type Presence = {
  online: boolean;
  /** Dernier signal, `null` si inconnu (jamais vu, présence d'un groupe). */
  lastSeenAt: string | null;
};

const time = (date: Date) =>
  date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * « En ligne », ou depuis quand la personne a été vue : à la minute dans l'heure, à l'heure
 * aujourd'hui et hier, au jour dans la semaine. Au-delà, rien n'est dit.
 */
export function presenceLabel(
  presence: Presence | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!presence) return null;
  if (presence.online) return 'En ligne';
  if (!presence.lastSeenAt) return null;
  const seen = new Date(presence.lastSeenAt);
  if (Number.isNaN(seen.getTime())) return null;
  const minutes = Math.floor((now.getTime() - seen.getTime()) / 60_000);
  if (minutes < 1) return 'Vu à l’instant';
  if (minutes < 60) return `Vu il y a ${minutes} min`;
  if (sameDay(seen, now)) return `Vu aujourd’hui à ${time(seen)}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(seen, yesterday)) return `Vu hier à ${time(seen)}`;
  if (minutes < 7 * 24 * 60)
    return `Vu le ${seen.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}`;
  return null;
}
